import { Group, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

export interface FilterBarProps {
  /** The search box's text. A screen with nothing to search by name (Reports) leaves both search props out. */
  readonly search?: string;
  readonly onSearchChange?: (value: string) => void;
  readonly searchPlaceholder?: string;
  /** Filter controls rendered beside the search box. */
  readonly filters?: ReactNode;
  /** Actions pinned right; they wrap below the filters at mid-desktop widths instead of crushing them. */
  readonly actions?: ReactNode;
}

/**
 * The one filter-row pattern for list screens: search left (when the screen searches), filters beside it, actions
 * right; every control stacks
 * full-width below 640px. The filter group carries `flex: 1 1 560px` on desktop and cancels it on phones, where a
 * flex-basis would become height (docs/UI_STANDARDS.md).
 */
export function FilterBar({
  search = "",
  onSearchChange,
  searchPlaceholder = "Search",
  filters,
  actions,
}: FilterBarProps) {
  // The box keeps its own text. The search a screen passes back usually lives in the page address, which arrives a
  // moment after each keystroke; were the box to show that directly, a letter typed in the gap would be lost ("abern"
  // became "abrn" on staging, 2026-09-27). What comes back is either an echo of something typed here, ignored, or a
  // change from elsewhere (clearing the filters, the browser's back button), which the box then shows.
  const [text, setText] = useState(search);
  const typed = useRef<string[]>([]);
  useEffect(() => {
    const echo = typed.current.indexOf(search);
    if (echo >= 0) {
      typed.current = typed.current.slice(echo + 1);
      return;
    }
    typed.current = [];
    setText(search);
  }, [search]);
  return (
    <Group
      justify="space-between"
      align="flex-start"
      wrap="wrap"
      gap="sm"
      mb="md"
      className="how-filter-bar"
    >
      <Group gap="sm" wrap="wrap" align="flex-start" className="how-filter-bar__filters">
        {onSearchChange !== undefined && (
          <TextInput
            value={text}
            onChange={(event) => {
              const next = event.currentTarget.value;
              setText(next);
              typed.current = [...typed.current, next].slice(-20);
              onSearchChange(next);
            }}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            leftSection={<IconSearch size={16} aria-hidden />}
            className="how-filter-bar__search"
          />
        )}
        {filters}
      </Group>
      {actions !== undefined && (
        <Group gap="sm" wrap="wrap" className="how-filter-bar__actions">
          {actions}
        </Group>
      )}
    </Group>
  );
}
